import { z } from 'zod';
import { RecordIdSchema } from './identifiers';
import { InstantSchema } from './time';

/**
 * Controlled data reset (Settings → Data reset). Never a "delete everything" button:
 *
 * - The requester chooses exact scopes from a fixed catalogue; each shows what it contains and how
 *   many records. Financial, contractual, inventory-history, audit, issued and posted records are in
 *   no scope and can never be reset. In production only operational scopes are offered.
 * - An independent approval through the engine (`data.reset`, segregated, refused without a policy),
 *   then a 24-hour wait after the approval.
 * - Execution is a separate act: second factor, reason and the typed confirmation phrase; the
 *   approval, the wait and the scope are re-checked — a material change of what the scopes contain
 *   since the request invalidates it. A verified backup of everything to be removed is written and
 *   read back first; if it does not verify, nothing is removed.
 * - One reset runs at a time, and while it runs every other write is refused. The request, the
 *   backup's checksum and the outcome stay in the protected audit trail.
 * - A clean install is a different path (client-init, bootstrap-admin), not a reset.
 */

export const DATA_RESET_SCOPES = [
  /** Leads never converted, with their activities; leads with papers are kept. */
  'unconvertedLeads',
  /** Tasks and their history. */
  'tasks',
  /** Notification inbox entries and delivery attempts (operational). */
  'notifications',
  /** Import batches and per-row results — never the source keys that keep imports idempotent. */
  'importHistory',
  /** Manually recorded campaign performance figures. */
  'marketingFigures',
] as const;
export type DataResetScope = (typeof DATA_RESET_SCOPES)[number];
export const DataResetScopeSchema = z.enum(DATA_RESET_SCOPES);

/** Scopes that may be reset in a production deployment: operational records only. */
export const PRODUCTION_RESET_SCOPES: readonly DataResetScope[] = [
  'notifications',
  'importHistory',
];

export const DATA_RESET_STATES = [
  'pendingApproval',
  'approved',
  'executing',
  'executed',
  'rejected',
  'cancelled',
  'invalidated',
  'failed',
] as const;

export const DataResetSchema = z.strictObject({
  resetId: RecordIdSchema,
  number: z.string(),
  scopes: z.array(DataResetScopeSchema),
  /** What each scope contained when requested. */
  counts: z.record(z.string(), z.number().int().nonnegative()),
  reason: z.string(),
  state: z.enum(DATA_RESET_STATES),
  requestedByAccountId: z.string(),
  approvalRequestId: z.string().optional(),
  approvedAt: InstantSchema.optional(),
  /** The earliest moment execution is allowed: 24 hours after the approval. */
  executableFrom: InstantSchema.optional(),
  /** The phrase the executor types, exactly. */
  confirmationPhrase: z.string(),
  backup: z
    .strictObject({
      key: z.string(),
      sha256: z.string(),
      records: z.number().int(),
      verified: z.boolean(),
    })
    .optional(),
  removed: z.record(z.string(), z.number().int().nonnegative()).optional(),
  executedByAccountId: z.string().optional(),
  executedAt: InstantSchema.optional(),
  outcomeReason: z.string().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type DataReset = z.infer<typeof DataResetSchema>;

export const DataResetPreviewSchema = z.strictObject({
  scopes: z.array(
    z.strictObject({
      scope: DataResetScopeSchema,
      count: z.number().int().nonnegative(),
      allowed: z.boolean(),
    }),
  ),
  production: z.boolean(),
});

export const RequestDataResetSchema = z.strictObject({
  scopes: z.array(DataResetScopeSchema).min(1).max(DATA_RESET_SCOPES.length),
  reason: z.string().trim().min(10).max(500),
  idempotencyKey: z.string().trim().min(8).max(200),
});

export const ExecuteDataResetSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(10).max(500),
  confirmationPhrase: z.string().trim().min(5).max(60),
});

export const CancelDataResetSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500),
});

export const DATA_RESET_APPROVAL_OPERATION = 'data.reset' as const;

export const DATA_RESET_AUDIT_ACTIONS = {
  requested: 'data.reset.requested',
  decided: 'data.reset.decided',
  cancelled: 'data.reset.cancelled',
  invalidated: 'data.reset.invalidated',
  backedUp: 'data.reset.backedUp',
  executed: 'data.reset.executed',
  failed: 'data.reset.failed',
} as const;

/** The 24-hour cooling period between approval and execution. */
export const DATA_RESET_WAIT_HOURS = 24;
